// ADR-134 D1 — the file kinds a workflow changeset may contain.
//
// Split out of ChangesetFiles.tsx so that file exports only its component
// (react-refresh), and so these can be unit-tested without rendering.

/** The kinds the workflow editor may add, matching the server's
 *  proposableActivityPath. config/fleet.yaml is deliberately absent: profile
 *  edits are a different surface with a different review.
 *
 *  ADR-159 D3 — this list claimed to match the server and did not. It never gained
 *  ADR-154's toolchain/activity-commands.yaml, and its 'skill' kind produced only
 *  SKILL.md while the server has always accepted ANY file under skills/ — so a
 *  skill-owned script (clarity_append.py) could not be added by hand at all. The
 *  parity is now asserted by the backend's TestEmissionContractMatchesTheGate,
 *  which reads this file, rather than left to this comment to be true. */
export type ExtraFileKind =
  | 'cron'
  | 'activity'
  | 'skill'
  | 'python'
  | 'skill-file'
  | 'resource'
  | 'allowlist'

/** Kinds whose path is fixed, so the picker asks for no name. */
export const KINDS_WITHOUT_NAME: ReadonlySet<ExtraFileKind> = new Set<ExtraFileKind>([
  'cron',
  'allowlist',
])

export const EXTRA_FILE_KINDS: { kind: ExtraFileKind; label: string; hint: string }[] = [
  {
    kind: 'cron',
    label: 'Schedule (crons.yaml)',
    hint: "Point a schedule at a workflow. A cron's inputs are static, so a scheduled sweep must target a discovery activity, not a per-item workflow.",
  },
  {
    kind: 'activity',
    label: 'Another workflow',
    hint: 'A second activity in the same change — e.g. the discovery workflow that fans out into this one.',
  },
  {
    kind: 'skill',
    label: 'Skill (SKILL.md)',
    hint: 'A skill that routes to a workflow. Rename a workflow and its route must move in the same change, or the route is orphaned.',
  },
  {
    // ADR-111 IA-8. A python task's `file` names a step body in the SAME
    // activities directory, and the validator requires it to exist — so a draft
    // that adds a python step and the file that step runs have to travel
    // together or neither can land.
    kind: 'python',
    label: 'Python step body (.py)',
    hint: 'The code an ADR-080 python task runs. It must live in this agent\'s activities/ folder — that is the only place the runtime looks for it (agents/<target>/activities/), so it cannot be shared from a skill or another agent. Add it here and the step and its code land in one PR.',
  },
  {
    // ADR-159 D3. The server's skills branch is `parts[2] == "skills"` at depth
    // >= 4 — any file, any depth. The 'skill' kind above only ever built
    // SKILL.md, so a skill-owned script was unreachable from this picker even
    // though every enforcing gate accepted it.
    kind: 'skill-file',
    label: 'File inside a skill',
    hint: 'A script or asset that lives beside a SKILL.md — the shared code a command step runs (skills/report-pipeline/clarity_append.py). Give the path under skills/, e.g. report-pipeline/clarity_append.py.',
  },
  {
    // ADR-159 D1.
    kind: 'resource',
    label: 'Shared resource (resources/)',
    hint: "The fleet's shared reference data — a property registry or report config a workflow reads at run time. A workflow that expects a new key and the file that supplies it have to land together, or the run fails on the missing key.",
  },
  {
    // ADR-154 shipped the server half; this picker never gained the kind.
    kind: 'allowlist',
    label: 'Command allowlist (activity-commands.yaml)',
    hint: 'The entry that lets a command step resolve its name (ADR-064 D4). A command step is a hard validation error without it, so the two are one change. An operator approves this entry — drafting it here is what gives them something to approve.',
  },
]

/** pathFor builds the repo path for a kind. Kept here so the UI and the server's
 *  allow-list cannot drift into disagreeing about what a "skill" path looks like. */
export function pathFor(kind: ExtraFileKind, agent: string, name: string): string {
  switch (kind) {
    case 'cron':
      return `agents/${agent}/crons.yaml`
    case 'activity':
      return `agents/${agent}/activities/${name}.yaml`
    case 'skill':
      return `agents/${agent}/skills/${name}/SKILL.md`
    case 'python':
      return `agents/${agent}/activities/${name}.py`
    case 'skill-file':
      return `agents/${agent}/skills/${name}`
    case 'resource':
      return `resources/${name}`
    case 'allowlist':
      return 'toolchain/activity-commands.yaml'
  }
}

/** A workflow/skill slug: lowercase, hyphen-separated. */
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/

/** A python step-body stem: lowercase, UNDERSCORE-separated.
 *
 *  Deliberately not SLUG. Every step body in the fleet is underscore-named
 *  (pipeline_step.py, collect_ga4.py, assemble_step.py) because these files are
 *  also imported as modules by their siblings, and a hyphen makes a module name
 *  unimportable. Validating a .py against the workflow slug rule would push the
 *  operator toward `collect-ga4.py`, which is the one spelling that cannot be
 *  imported. */
const PY_STEM = /^[a-z][a-z0-9_]*$/

/** A path under skills/: a skill slug, then at least one more segment.
 *  `report-pipeline/clarity_append.py`. The server requires depth >= 4 from the
 *  repo root, which is exactly "slug + something". */
const SKILL_SUBPATH = /^[a-z0-9]+(-[a-z0-9]+)*(\/[A-Za-z0-9._-]+)+$/

/** A path under resources/: one or more plain segments. */
const RESOURCE_SUBPATH = /^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/

/** normalizeName trims the name and, for a python body, tolerates a typed
 *  `.py` — pathFor appends the extension, so keeping it would yield `x.py.py`. */
export function normalizeName(kind: ExtraFileKind, raw: string): string {
  const name = raw.trim()
  if (kind === 'python' && name.toLowerCase().endsWith('.py')) {
    return name.slice(0, -3)
  }
  return name
}

/** validateName returns an operator-facing error for a bad name, or null.
 *  Lives here rather than in the component so the rule is unit-testable and the
 *  two name shapes cannot drift apart. */
export function validateName(kind: ExtraFileKind, name: string): string | null {
  if (KINDS_WITHOUT_NAME.has(kind)) return null
  if (!name) return 'Name the new file.'
  // `..` would climb out of the prefix pathFor builds; the server refuses it too,
  // but an operator should see why here rather than as a rejected changeset.
  if (name.split('/').includes('..')) return 'A path segment cannot be "..".'
  if (kind === 'skill-file') {
    return SKILL_SUBPATH.test(name)
      ? null
      : 'Give the path under skills/ — a skill slug then a file, e.g. report-pipeline/clarity_append.py.'
  }
  if (kind === 'resource') {
    return RESOURCE_SUBPATH.test(name)
      ? null
      : 'Give the path under resources/, e.g. properties/report-config.json.'
  }
  if (kind === 'python') {
    return PY_STEM.test(name)
      ? null
      : 'Use a lowercase, underscore-separated name (e.g. collect_ga4). A python step body is imported as a module, so hyphens will not work.'
  }
  return SLUG.test(name)
    ? null
    : 'Use a lowercase, hyphen-separated name (e.g. asana-ticket-inspection).'
}
